//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { CopyrightedIconWithTooltip } from '@components/atoms/overlay-icon/CopyrightedIcon';
import gifSvg from '@images/content-library/gif_on.svg';
import gallerySvg from '@images/instagram-gallery-shadow.svg';
import reelsSvg from '@images/instagram-reels-shadow.svg';
import storySvg from '@images/instagram-story-shadow.svg';
import igtvSvg from '@images/instagram-tv.svg';
import videoSvg from '@images/instagram-video-shadow.svg';
import pinterestSvg from '@images/pinterest-shadow.svg';
import tiktokSvg from '@images/tiktok-shadow.svg';
import twitterSvg from '@images/twitter-shadow.svg';
import { isDefined } from '@utils/filterPredicates';

export interface IMediaTypeOverlayIconProps
  extends React.HTMLAttributes<HTMLDivElement> {
  type: MediaTypeOverlayIconType;
  isCopyrighted?: boolean;
}

export type MediaTypeOverlayIconType =
  | 'gallery'
  | 'gif'
  | 'IGTV'
  | 'image'
  | 'pinterest'
  | 'Reels'
  | 'story'
  | 'tiktok'
  | 'twitter'
  | 'video';

const MediaTypeOverlaySvg: Record<
  MediaTypeOverlayIconType,
  string | undefined
> = {
  gallery: gallerySvg,
  gif: gifSvg,
  IGTV: igtvSvg,
  image: undefined,
  pinterest: pinterestSvg,
  Reels: reelsSvg,
  story: storySvg,
  tiktok: tiktokSvg,
  twitter: twitterSvg,
  video: videoSvg,
} as const;

/**
 * The MediaTypeOverlayIcon atom renders a media type icon on top of
 * the Media atom (e.g. video, story, etc)
 */
export const MediaTypeOverlayIcon = ({
  className,
  isCopyrighted = false,
  type,
}: IMediaTypeOverlayIconProps) => {
  const svg = MediaTypeOverlaySvg[type];
  return (
    <MediaTypeImageContainer className={className}>
      {isCopyrighted && <StyledCopyrightIconWithTooltip />}
      {isDefined(svg) ? <StyledImage alt='' src={svg} /> : <></>}
    </MediaTypeImageContainer>
  );
};

const MediaTypeImageContainer = styled.div(
  ({ theme }) => `
  display: flex;
  height: ${theme.spacings[5]};
  border-radius: ${theme.borderRadii.pill};
  align-items: center;
  justify-content: flex-end;
  flex-direction: row;
  width: 100%;
  position: relative;
  padding-right: ${theme.spacings[2]};
`,
);

const StyledImage = styled.img`
  width: 22px;
  height: 22px;
`;

// Needs a z-index of 10 to allow the tooltip to show on hover higher than MetadataLayer
const StyledCopyrightIconWithTooltip = styled(CopyrightedIconWithTooltip)`
  position: relative;
  display: block;
  z-index: 10;
`;
